import { isWeekend } from '@frontend/common';
import { ICONS } from '@frontend/icons';
import { format } from 'date-fns';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { onQueryParamChange } from '../select/object-select-input/object-select-input.controller';
import useDateInput from './date-input.controller';

export interface DateInputProps {
    id?: string;
    required?: boolean;
    className?: string;
    valid?: boolean;
    isValidCallback?: (valid: boolean) => void;
    onChange?: (value: Date | undefined) => void;
    onFocusChange?: () => void;
    value?: Date;
    errorMessage?: React.ReactNode;
    label?: React.ReactNode;
    helpMessage?: React.ReactNode;
    submitted: boolean;
    disabled?: boolean;

    disallowWeekends?: boolean;
    addedValidCheck?: (date: Date | undefined) => boolean;
    minDate?: Date;
    maxDate?: Date;
    dateTime?: boolean;
    allowURLChange?: boolean;
    queryParamKey?: string;
}

export const DateInput = (props: DateInputProps) => {
    const viewProps = useDateInput(props);

    return (
        <div className={`flex w-full flex-col items-start justify-start gap-1 ${props.className ? props.className : ''}`}>
            {props.label && (
                <span className='text-foreground-default text-sm font-medium'>
                    {props.required ? <span className='text-status-danger me-1'>&#9679;</span> : null}
                    {props.label}
                </span>
            )}
            {props.helpMessage && <small className='form-text text-muted'>{props.helpMessage}</small>}
            <div className='outline-border-default bg-background-surface flex h-11 w-full items-center justify-between overflow-hidden rounded-xl px-4 outline-1 outline-offset-[-1px]'>
                <input
                    className={`select-none ${props.value ? 'text-foreground-default' : 'text-brand-muted'} border-none text-base font-medium outline-none`}
                    id={props.id ? props.id : 'date-input'}
                    value={props.value ? format(props.value, viewProps.format) : ''}
                    type={props.dateTime ? 'datetime-local' : 'date'}
                    required={props.required}
                    onFocus={() => {
                        viewProps.changeTouched(true);
                        props.onFocusChange && props.onFocusChange();
                    }}
                    onChange={(e) => {
                        if (e.target.value.length === 0) {
                            props.onChange && props.onChange(undefined);
                            props.allowURLChange && onQueryParamChange(null, props.queryParamKey ?? 'date', viewProps.setSearchParams);
                        } else {
                            props.onChange && props.onChange(new Date(e.target.value));
                            props.allowURLChange &&
                                onQueryParamChange(
                                    format(new Date(e.target.value), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
                                    props.queryParamKey ?? 'date',
                                    viewProps.setSearchParams
                                );
                        }
                        viewProps.isValid();
                    }}
                    onClick={viewProps.openCalendar}
                    min={props.minDate ? format(props.minDate, viewProps.format) : undefined}
                    max={props.maxDate ? format(props.maxDate, viewProps.format) : undefined}
                    disabled={props.disabled}
                />
                <div className='flex gap-2'>
                    {props.value !== undefined && (
                        <ICONS.BUTTON.CLOSE
                            className='text-brand-muted hover:text-foreground-default relative hover:cursor-pointer'
                            onClick={viewProps.clear}
                        />
                    )}
                    <ICONS.BUTTON.CALENDAR
                        className='hover:text-foreground-default relative cursor-pointer'
                        onClick={viewProps.openCalendar}
                    />
                </div>
            </div>
            {viewProps.valid === false && viewProps.touched === true && (
                <>
                    {props.disallowWeekends === true && props.value && isWeekend(props.value) ? (
                        <span className='badge bg-status-danger mt-2'>
                            <small>{'Weekends not allowed'}</small>
                        </span>
                    ) : (
                        <>
                            {props.minDate && props.value && props.minDate.getTime() > props.value.getTime() ? (
                                <span className='badge bg-status-danger mt-2'>
                                    <small>
                                        <FormattedMessage
                                            id='DateTimeInput.HourSelectedToEarly'
                                            description='The error message on the DateTimePicker when the selected time is before the allowed dateTime.'
                                            defaultMessage='You cannot schedule this early. Please pick an moment after {time}.'
                                            values={{
                                                time:
                                                    props.minDate &&
                                                    props.minDate.getHours() +
                                                        ':' +
                                                        (props.minDate.getMinutes() < 10 ? '0' + props.minDate.getMinutes() : props.minDate.getMinutes())
                                            }}
                                        />
                                    </small>
                                </span>
                            ) : (
                                <span className='badge bg-status-danger mt-2'>
                                    <small>{props.errorMessage ? props.errorMessage : 'Please enter a valid date and time.'}</small>
                                </span>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};
